/**
 * Hook `useStrategies`
 * 
 * Este hook proporciona funcionalidades para gestionar estrategias, incluyendo cargar estrategias, crear nuevas, establecer filtros y actualizar estrategias existentes.
 * 
 * @returns {object} Un objeto que contiene el estado actual de las estrategias y funciones para manejar dichas estrategias.
 */

import { useDispatch, useSelector } from 'react-redux'
import { RootState, AppDispatch } from 'src/redux/index'
import { useAlert } from 'src/hooks/useAlert'
import useLoading from 'src/hooks/useLoading'
import {
  setFilter,
  setStrategy,
} from 'src/redux/Dashboard/Strategies/StrategiesSlice'
import {
  IStrategy,
  IFilterStrategies,
} from 'src/services/dashboard/strategies/strategies.interface'
import {
  createStrategy,
  getStrategies,
} from 'src/services/dashboard/strategies/strategies.service'

export const useStrategies = () => {
  const { showAlert } = useAlert()
  const { startLoading, stopLoading } = useLoading()
  const dispatch = useDispatch<AppDispatch>()
  const { strategy, strategies, filter } = useSelector(
    (state: RootState) => state.strategy,
  )

  /**
   * Carga la lista de estrategias con base en los parámetros de paginación proporcionados.
   * Muestra alertas según el resultado de la operación.
   *
   * @function loadStrategies
   * @param {object} pagination - Parámetros de paginación y filtros para cargar las estrategias.
   * @param {number} pagination.currentPage - Página actual para la paginación.
   * @param {number} pagination.pageSize - Tamaño de la página para la paginación.
   * @param {number} [pagination.indicatorId] - ID del indicador para filtrar estrategias.
   * @param {number} [pagination.channelId] - ID del canal para filtrar estrategias.
   * @throws {Error} Si ocurre un error inesperado durante la carga de las estrategias.
   */
  const loadStrategies = (pagination: {
    currentPage?: number
    pageSize?: number
    indicatorId?: number
    channelId?: number
  }) => {
    try {
      startLoading()
      dispatch(getStrategies(pagination))
        .then((result: any) => {
          if (getStrategies.rejected.match(result)) {
            showAlert(
              'error',
              String(result.payload) || 'Error al cargar las estrategias',
            )
          } else if (getStrategies.fulfilled.match(result)) {
            showAlert('success', 'Estrategias cargadas correctamente')
          }
        })
        .finally(() => {
          stopLoading()
        })
      showAlert('success', 'Estrategias cargadas correctamente')
    } catch (error: any) {
      showAlert('error', error.message || 'Ocurrió un error inesperado')
    }
  }

  /**
   * Crea una nueva estrategia.
   * Muestra alertas según el resultado de la operación.
   *
   * @function createNewStrategy
   * @param {IStrategy} strategy - Los datos de la estrategia que se va a crear.
   * @throws {Error} Si ocurre un error inesperado durante la creación de la estrategia.
   */
  const createNewStrategy = (strategy: IStrategy) => {
    try {
      dispatch(createStrategy(strategy))
        .then((result: any) => {
          stopLoading()
          if (createStrategy.rejected.match(result)) {
            showAlert(
              'error',
              String(result.payload) || 'Error al cargar las preguntas',
            )
          } else if (createStrategy.fulfilled.match(result)) {
            showAlert('success', 'Preguntas cargadas correctamente')
          }
        })
        .finally(() => {
          stopLoading()
        })
      showAlert('success', 'Estrategia creada correctamente')
    } catch (error: any) {
      showAlert('error', error.message || 'Ocurrió un error inesperado')
    }
  }

  /**
   * Establece el filtro de estrategias en el estado.
   *
   * @function setStrategiesFilter
   * @param {IFilterStrategies} filter - El filtro que se aplicará a las estrategias.
   */
  const setStrategiesFilter = (filter: IFilterStrategies) => {
    dispatch(setFilter(filter))
  }

  /**
   * Actualiza una estrategia existente en el estado.
   *
   * @function updateStrategy
   * @param {IStrategy} strategy - Los datos de la estrategia que se va a actualizar.
   */
  const updateStrategy = (strategy: IStrategy) => {
    dispatch(setStrategy(strategy))
  }

  return {
    strategy,
    strategies,
    filter,
    createNewStrategy,
    loadStrategies,
    setStrategiesFilter,
    updateStrategy,
  }
}

export default useStrategies
